import buildUrl from 'build-url';
import { Config } from '../../../../../common/types/config';
import { ListingPageUrlState, FilterType } from '../../../../redux/products/types';
import { SeoData, LanguageLocalizedFilters, LocalizedFiltersData } from '../../../../redux/seo/types';
import { convertListingPageStateToUrl } from '../listingPageUrlService';
import { PageType } from '../../../../hooks/UrlManager/types';
import { PdpSeoData } from '../../../../redux/productdetail/types';
import { hasPathFilter, getAllAvailableIn, getHrefDomain } from './utils';
import { SEO_CONSTANTS } from './constant';
import { PartialRouterContext } from './types';
import { AppliedFilter } from 'Client/components/Generic/FilterAndSortBox/types';

function translateAppliedFilters(
    { appliedFilters }: ListingPageUrlState,
    localizedFilter: LanguageLocalizedFilters
): AppliedFilter<FilterType>[] {
    return appliedFilters
        .filter(filter => localizedFilter && localizedFilter[filter.name])
        .map(filter => ({ ...filter, values: localizedFilter[filter.name].values } as AppliedFilter<FilterType>));
}

const getAllLocalizedFilters = (filters: LocalizedFiltersData): LocalizedFiltersData => {
    return {
        ...filters,
        at: filters.de,
    };
};

const createHrefLangPLPUrlState = (
    urlState: ListingPageUrlState,
    availableIn: string,
    localizedFilter?: LanguageLocalizedFilters
) => {
    const isPLPWithCategoryFilter = urlState.listingPageType === PageType.NEWPRODUCTS;

    const baseHrefLangState = {
        ...urlState,
        page: hasPathFilter(urlState) ? 1 : urlState.page,
        sortingParams: {},
        search: undefined,
        appliedFilters: translateAppliedFilters(urlState, localizedFilter || {}),
    };

    if (isPLPWithCategoryFilter) {
        const [categoryFilters] = urlState.appliedFilters.filter(filter => filter.type === FilterType.categories);
        const categoryFilter = {
            name: '',
            type: FilterType.categories,
            categoryFilters,
            values: [availableIn],
        };
        baseHrefLangState.appliedFilters.push(categoryFilter);
    } else {
        baseHrefLangState.listingPageKey = availableIn;
    }

    return baseHrefLangState;
};

const buildAlternateHrefs = (seoData: SeoData, urlState: ListingPageUrlState, config: Config) => {
    const { availableCountryCodes } = config;
    const allAvailableIn = getAllAvailableIn(seoData.availableIn);
    const allTranslatedFilters = getAllLocalizedFilters(seoData.localizedFilters);

    const alternateHrefs = Object.keys(allAvailableIn).map(country => {
        const hrefLangCountry = availableCountryCodes[country];
        const availableIn = allAvailableIn[country];
        const translatedFilter = allTranslatedFilters[country];
        const seoHrefLangPLPUrlState = createHrefLangPLPUrlState(urlState, availableIn, translatedFilter);
        return {
            rel: SEO_CONSTANTS.alternateKeyLink,
            hrefLang: hrefLangCountry,
            href: convertListingPageStateToUrl(seoHrefLangPLPUrlState, getHrefDomain(config, country)),
        };
    });
    return alternateHrefs;
};

const buildDefaultDEHrefLang = (seoData: SeoData, urlState: ListingPageUrlState, config: Config) => {
    return {
        rel: SEO_CONSTANTS.alternateKeyLink,
        hrefLang: 'de',
        href: convertListingPageStateToUrl(
            createHrefLangPLPUrlState(urlState, seoData.availableIn.de, seoData.localizedFilters?.de),
            getHrefDomain(config, 'de')
        ),
    };
};

export const buildCanonicalObject = (urlState: ListingPageUrlState, seoData: SeoData, host: string): object => {
    const copyUrlState = {
        ...urlState,
        search: undefined,
        sortingParams: {},
    };

    const href = seoData.canonical ? seoData.canonical : convertListingPageStateToUrl(copyUrlState, host);
    return { rel: SEO_CONSTANTS.canonicalKeyLink, href };
};

export const hrefLang = (seoData: SeoData | undefined, config: Config, urlState: ListingPageUrlState) => {
    const hasAvailableIn = Object.keys(seoData?.availableIn || {}).length > 0;

    if (seoData && hasAvailableIn) {
        const canonicalLinkObject = buildCanonicalObject(urlState, seoData, config.links.url);
        if (hasPathFilter(urlState) && urlState.page > 1) {
            return [canonicalLinkObject];
        }

        return [
            canonicalLinkObject,
            buildDefaultDEHrefLang(seoData, urlState, config),
            ...buildAlternateHrefs(seoData, urlState, config),
        ];
    }

    return [];
};

export const pdpHrefLang = (seoData: PdpSeoData, config: Config, router: PartialRouterContext) => {
    const hasAvailableIn = (Object.keys((seoData || { availableIn: {} }).availableIn) || []).length > 0;
    const {
        availableCountryCodes,
        links: { url },
    } = config;
    const {
        location: { pathname },
    } = router;

    if (seoData && hasAvailableIn) {
        const availableIn = getAllAvailableIn(seoData.availableIn);
        const defaultLinks = [
            {
                rel: SEO_CONSTANTS.canonicalKeyLink,
                href: buildUrl(url, { path: pathname }),
            },
            {
                rel: SEO_CONSTANTS.alternateKeyLink,
                hrefLang: 'de',
                href: buildUrl(getHrefDomain(config, 'de'), { path: availableIn.de }),
            },
        ];

        const alternateHrefs = Object.keys(availableIn).map(country => {
            const hrefLangCountry = availableCountryCodes[country];
            const countrySpecificPathname = availableIn[country];

            return {
                rel: SEO_CONSTANTS.alternateKeyLink,
                hrefLang: hrefLangCountry,
                href: buildUrl(getHrefDomain(config, country), { path: countrySpecificPathname }),
            };
        });
        return [...defaultLinks, ...alternateHrefs];
    }

    return [];
};

export const homePageHrefLang = (config: Config) => {
    const canonicalHref = config.links.url;
    const canonicalLinkObject = { rel: SEO_CONSTANTS.canonicalKeyLink, href: canonicalHref };
    const defaultDEHrefLang = {
        rel: SEO_CONSTANTS.alternateKeyLink,
        hrefLang: 'de',
        href: config.links.url,
    };

    const alternateHrefs = Object.keys(config.availableCountryCodes).map(country => {
        const hrefLangCountry = config.availableCountryCodes[country];
        return {
            rel: SEO_CONSTANTS.alternateKeyLink,
            hrefLang: hrefLangCountry,
            href: buildUrl(getHrefDomain(config, country), { path: '/' }),
        };
    });

    return [canonicalLinkObject, defaultDEHrefLang, ...alternateHrefs];
};
